import { Button, Col, Row, Upload, message } from "antd";
import moment from "moment";
import { React, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HasAccess } from "@permify/react-role";
import { patientsService } from "../../../services/patient";
import { allowedFileCriteria, uploadAssetController } from "../../../utils/fileUtils";
import { getAsset } from "../../../services/general";
import { PaperClipOutlined } from "@ant-design/icons";
import UploadConsentModal from "./UploadConsentModal";



const UploadedConsents = ({ admissions, consents, patientId, fetchConsents }) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const openModal = () => setVisible(true);
  const closeModal = () => setVisible(false)
  const typeMapper = {
    "normal": t("patientPage.consentDetail.normalType"),
    "psychological": t("patientPage.consentDetail.psychologicalType"),
    "drugs": t("patientPage.consentDetail.drugsType"),
    "drug": t("patientPage.consentDetail.drugsType"),
    "stopBang": t("patientPage.consentDetail.stopBangType"),
    "isl": t("patientPage.consentDetail.islType"),
    "other": t("patientPage.consentDetail.othersType"),
  }

  const loadFromMapper = {
    "admission": t("patientPage.consentDetail.admission"),
    "patientFile": t("patientPage.consentDetail.patientFile"),
  }

  const formatDate = (date) => {
    return moment(date, "YYYY-MM-DDTHH:mm:ssZ").format("DD-MM-YYYY");
  };

  const uploadConsent = async (
    admissionPatientId,
    consentingType,
    file,
    setLoading,
    consentingId
  ) => {
    try {
      let idDocument = consentingId
      setLoading((loading) => {
        return { ...loading, upload: true };
      });
      if (!allowedFileCriteria(t, file)) {
        throw new Error();
      }
      if (!idDocument) {
        const response = await patientsService.uploadConsent({
          admissionPatientId,
          patientId,
          consentingType,
          loadFrom: "patientFile",
        });
        idDocument = response
      }
      if (idDocument) {
        await uploadAssetController({
          file,
          object_field: "document",
          object_id: idDocument,
          object_type: "Consenting",
        });
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: t("patientPage.consentDetail.uploadError"),
        style: { marginTop: "35vh", fontSize: 16 },
      });
    } finally {
      await fetchConsents();
      setLoading((loading) => {
        return { ...loading, upload: false };
      });
    }
  };

  const downloadConsent = async (documentId, setLoading) => {
    try {
      setLoading((loading) => {
        return { ...loading, download: true };
      });
      const { document_url } = await getAsset({
        field: "document",
        type: "Consenting",
        id: documentId,
      });
      window.open(document_url, "_blank");
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno: ${error}`,
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    } finally {
      setLoading((loading) => {
        return { ...loading, download: false };
      });
    }
  };

  const reservationList = useMemo(() => {
    if (admissions?.length) {
      return admissions
        .map(admission => ({
          value: admission.admission_patient_id,
          label: admission.reservation_number + ' - ' + formatDate(admission.admission_date)
        }))
        .filter((item, index, self) =>
          index === self.findIndex(t => t.value === item.value)
        );
    }
    return [];
  }, [admissions]);

  const customTableHeader = () => {
    return (
      <Row className="table-header">
        <Col lg={4} className="align-item-center-start p-10">
          <h3>{t("patientPage.consentDetail.reservation")}</h3>
        </Col>
        <Col lg={4} className="center-item p-10">
          <h3 className="text-center">{t("patientPage.consentDetail.admissionDate")}</h3>
        </Col>
        <Col lg={4} className="center-item p-10">
          <h3 className="text-center">
            {t("patientPage.consentDetail.type")}
          </h3>
        </Col>
        <Col lg={4} className="center-item p-10">
          <h3 className="text-center">
            {t("patientPage.consentDetail.signedType")}
          </h3>
        </Col>
      </Row>
    );
  };

  const CustomTableBody = ({ item, index }) => {
    const [loading, setLoading] = useState({ upload: false, download: false });
    return (
      <Row className="table-body mb-15" key={index}>
        <Col lg={4} xs={12} className="align-item-center-start p-10">
          <h3 className="hide-desktop text-center">
            {t("patientPage.consentDetail.reservation")}
          </h3>
          <p className="mb-0">{item.reservation_number}</p>
        </Col>
        <Col lg={4} xs={12} className="center-item p-10">
          <h3 className="hide-desktop text-center">{t("patientPage.consentDetail.admissionDate")}</h3>
          <p className="mb-0">
            {item.created_at && formatDate(item.created_at)}
          </p>
        </Col>
        <Col lg={4} xs={24} className="center-item p-10">
          <h3 className="hide-desktop text-center">
            {t("patientPage.consentDetail.type")}
          </h3>
          <p className="mb-0">{typeMapper[item.consenting_type] || '-'}</p>
        </Col>
        <Col lg={4} xs={24} className="center-item p-10">
          <h3 className="hide-desktop text-center">
            {t("patientPage.consentDetail.signedType")}
          </h3>
          <p className="mb-0">{loadFromMapper[item.load_from] || '-'}</p>
        </Col>
        <Col lg={8} xs={24} className="center-item p-10">
          <h3 className="hide-desktop text-center">{t("patientPage.consentDetail.replaceConsentButton")}</h3>

          <HasAccess
            roles={[
              "super_usuario",
              "branch_office_manager",
              "equipo_psicologia",
              "doctor",
              "direccion_medica",
              "administracion",
              "equipo_medico",
            ]}
            permissions="downloadReport"
          >
            <Col
              xl={16}
              lg={16}
              xs={24}
              className="mr-10 upload-container"
              align="middle"
            >
              <Upload
                className="width-100"
                disabled={item.load_from === "admission"}
                beforeUpload={(file) => {
                  uploadConsent(
                    item.admission_patient_id,
                    item.consenting_type,
                    file,
                    setLoading,
                    item.id
                  );
                  return false;
                }}
                showUploadList={false}
              >
                <Button
                  disabled={item.load_from === "admission"}
                  className={`cmt-button secondary ${item.load_from === "admission"
                    ? "disabled outline"
                    : "secondary"
                    }`}
                  type="primary"
                  size="small"
                  loading={loading.upload}
                >
                  {t("patientPage.consentDetail.replaceConsentButton")}
                </Button>
              </Upload>
            </Col>
            <Col align="middle">
              <Button
                className={`cmt-button secondary outline ${item.id
                  ? "secondary outline"
                  : "disabled outline"
                  }`}
                type="secondary"
                disabled={!item.id}
                icon={<span className="icon-system-download" />}
                style={{
                  width: "30px",
                  whiteSpace: "normal",
                  height: "auto",
                  align: "middle",
                }}
                loading={loading.download}
                onClick={() =>
                  downloadConsent(item.id, setLoading)
                }
              />
            </Col>
          </HasAccess>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Row justify="center">
        <Col lg={22} xs={22} className="mt-30 mb-20">
          <Row justify="space-between">
            <Col>
              <h2 className="mb-15">
                {t("patientPage.consentDetail.loadedConsentsTitle")}
              </h2>
            </Col>
            <Col><Button
              className='cmt-button secondary outline'
              type="secondary"
              align="middle"
              onClick={openModal}
            >
              <PaperClipOutlined className="mr-5" style={{ fontSize: 22 }} />
              {t("patientPage.consentDetail.loadConsentButton")}
            </Button>

            </Col>
          </Row>
          {consents?.length > 0 ? (
            <>
              {customTableHeader()}
              {consents.map((item, index) => {
                return <CustomTableBody item={item} index={index} />;
              })}
            </>
          ) : (
            <p className="text-center">
              {t("patientPage.consentDetail.disclaimerConsent")}
            </p>
          )}
        </Col>
      </Row>
      <UploadConsentModal visible={visible} closeModal={closeModal} reservationList={reservationList} uploadFunction={uploadConsent}>
      </UploadConsentModal>
    </>
  );
};

export default UploadedConsents;
